<template>
  <div class="social-security-report">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="header-section">
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div>
              <h1 class="page-title">
                <v-icon large color="white" class="mr-3">mdi-shield-account</v-icon>
                รายงานข้อมูลลูกจ้าง งานประกันสังคม
              </h1>
              <p class="page-subtitle mt-2">
                ระบบติดตามและรายงานข้อมูลประกันสังคมของสถานศึกษา สังกัด สอศ.
              </p>
            </div>
            <div class="d-flex align-center header-badges">
              <v-chip class="mr-3 header-chip" dark>
                <v-icon left small>mdi-calendar</v-icon>
                ปีงบประมาณ {{ getCurrentYear() }}
              </v-chip>
              <v-chip class="header-chip total-chip" color="white" dark>
                <v-icon left small color="primary">mdi-account-group</v-icon>
                <span class="primary--text font-weight-bold">{{ formatNumber(totalEmployees) }} คน</span>
              </v-chip>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-primary" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">รวมลูกจ้างทั้งหมด</p>
                <h2 class="stat-value">{{ formatNumber(totalEmployees) }}</h2>
                <p class="stat-percent">คน</p>
              </div>
              <v-avatar size="56" color="primary" class="stat-icon">
                <v-icon dark size="28">mdi-account-group</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="100" 
              color="primary" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-success" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">มีประกันสังคม</p>
                <h2 class="stat-value">{{ formatNumber(statistics.withInsurance) }}</h2>
                <p class="stat-percent">{{ statistics.withInsurancePercent }}%</p>
              </div>
              <v-avatar size="56" color="success" class="stat-icon">
                <v-icon dark size="28">mdi-shield-check</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="statistics.withInsurancePercent" 
              color="success" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-warning" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">ไม่มีประกันสังคม</p>
                <h2 class="stat-value">{{ formatNumber(statistics.withoutInsurance) }}</h2>
                <p class="stat-percent">{{ statistics.withoutInsurancePercent }}%</p>
              </div>
              <v-avatar size="56" color="warning" class="stat-icon">
                <v-icon dark size="28">mdi-shield-alert</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="statistics.withoutInsurancePercent" 
              color="warning" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-info" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">ประเภททั้งหมด</p>
                <h2 class="stat-value">{{ formatNumber(statistics.totalTypes) }}</h2>
                <p class="stat-percent">รายการ</p>
              </div>
              <v-avatar size="56" color="info" class="stat-icon">
                <v-icon dark size="28">mdi-format-list-bulleted</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="100" 
              color="info" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section -->
    <v-row class="mb-6">
      <v-col cols="12" md="5">
        <v-card class="chart-card" elevation="4">
          <v-card-title class="chart-title">
            <v-icon color="primary" class="mr-2">mdi-chart-donut</v-icon>
            สัดส่วนประกันสังคม
          </v-card-title>
          <v-card-text>
            <apexchart
              ref="donutChart"
              type="donut"
              height="320"
              :options="donutChartOptions"
              :series="donutSeries"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card class="chart-card" elevation="4">
          <v-card-title class="chart-title">
            <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
            จำนวนลูกจ้างตามประเภทประกันสังคม
          </v-card-title>
          <v-card-text>
            <apexchart
              ref="barChart"
              type="bar"
              height="320"
              :options="barChartOptions"
              :series="barSeries"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-row>
      <v-col cols="12">
        <v-card class="data-table-card" elevation="4">
          <v-card-title class="table-title d-flex justify-space-between flex-wrap">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-table</v-icon>
              <span>รายละเอียดข้อมูลประกันสังคม</span>
            </div>
            <div class="d-flex align-center table-actions">
              <v-btn
                color="success"
                @click="exportToExcel()"
                :disabled="!budget_etcs.length"
              >
                <v-icon left>mdi-microsoft-excel</v-icon>
                ส่งออก Excel
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="ค้นหาข้อมูล..."
              outlined
              dense
              clearable
              hide-details
              class="mb-4 search-field"
            ></v-text-field>

            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="budget_etcs"
              :search="search"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 15, 25, -1],
                'items-per-page-text': 'แสดงรายการ:'
              }"
              class="modern-table"
              loading-text="กำลังโหลดข้อมูล..."
              no-data-text="ไม่พบข้อมูล"
              no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
            >
              <template v-slot:item.personnel_etc_safety="{ item }">
                <div class="d-flex align-center">
                  <v-avatar 
                    size="36" 
                    :color="getInsuranceColor(item.personnel_etc_safety)" 
                    class="mr-3"
                  >
                    <v-icon dark small>{{ getInsuranceIcon(item.personnel_etc_safety) }}</v-icon>
                  </v-avatar>
                  <span class="insurance-name">
                    {{ item.personnel_etc_safety || 'ไม่ระบุ' }}
                  </span>
                </div>
              </template>

              <template v-slot:item.countsafe="{ item }">
                <v-chip 
                  :color="getCountColor(item.countsafe)" 
                  dark 
                  small 
                  class="font-weight-bold"
                >
                  {{ formatNumber(item.countsafe) }}
                </v-chip>
              </template>

              <template v-slot:item.percent="{ item }">
                <div class="d-flex align-center">
                  <v-progress-circular
                    :value="calculatePercent(item.countsafe)"
                    :color="getPercentColor(calculatePercent(item.countsafe))"
                    size="45"
                    width="4"
                    class="mr-2"
                  >
                    <span class="percent-text">{{ calculatePercent(item.countsafe) }}</span>
                  </v-progress-circular>
                  <span class="percent-label">%</span>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";

export default {
  name: "SocialSecurityReport",

  components: {
    apexchart: VueApexCharts
  },

  data() {
    return {
      loading: true,
      ApiKey: "HRvec2021",
      valid: true,
      search: "",
      budget_etcs: [],
      totalEmployees: 0,

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Table Headers
      headers: [
        { text: "ประเภทประกันสังคม", align: "left", value: "personnel_etc_safety", width: "50%" },
        { text: "จำนวน (คน)", align: "center", value: "countsafe", width: "25%" },
        { text: "ร้อยละ", align: "center", value: "percent", width: "25%" }
      ],

      // Donut Chart Options
      donutChartOptions: {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        labels: [],
        colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014", "#BDBDBD", "#212121", "#F1B0B0"],
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Sarabun, sans-serif",
          markers: {
            width: 12,
            height: 12,
            radius: 2
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "14px",
                  fontFamily: "Sarabun, sans-serif"
                },
                value: {
                  show: true,
                  fontSize: "20px",
                  fontFamily: "Sarabun, sans-serif",
                  fontWeight: "bold",
                  formatter: function(val) {
                    return parseInt(val).toLocaleString() + " คน";
                  }
                },
                total: {
                  show: true,
                  label: "รวมทั้งหมด",
                  fontSize: "12px",
                  fontFamily: "Sarabun, sans-serif",
                  formatter: function(w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString() + " คน";
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString() + " คน";
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: { width: 280 },
            legend: { position: "bottom" }
          }
        }]
      },

      // Bar Chart Options
      barChartOptions: {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            barHeight: "60%",
            distributed: true
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "11px",
              fontFamily: "Sarabun, sans-serif"
            },
            formatter: function(val) {
              return parseInt(val).toLocaleString();
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: "11px",
              fontFamily: "Sarabun, sans-serif"
            },
            maxWidth: 200
          }
        },
        colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014", "#BDBDBD", "#212121", "#F1B0B0"],
        legend: { show: false },
        fill: { opacity: 1 },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toLocaleString() + " คน";
          },
          style: {
            fontSize: "11px",
            fontFamily: "Sarabun, sans-serif"
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString() + " คน";
            }
          }
        }
      }
    };
  },

  computed: {
    statistics() {
      const total = this.totalEmployees;
      const withInsurance = this.budget_etcs
        .filter(item => item.personnel_etc_safety && item.personnel_etc_safety !== "ไม่มี" && item.personnel_etc_safety !== "")
        .reduce((sum, item) => sum + (parseInt(item.countsafe) || 0), 0);
      const withoutInsurance = total - withInsurance;
      const totalTypes = this.budget_etcs.length;

      return {
        total,
        withInsurance,
        withoutInsurance,
        totalTypes,
        withInsurancePercent: total > 0 ? ((withInsurance / total) * 100).toFixed(1) : 0,
        withoutInsurancePercent: total > 0 ? ((withoutInsurance / total) * 100).toFixed(1) : 0
      };
    },

    donutSeries() {
      return this.budget_etcs.map(item => parseInt(item.countsafe) || 0);
    },

    barSeries() {
      return [{
        name: "จำนวน",
        data: this.budget_etcs.map(item => parseInt(item.countsafe) || 0)
      }];
    }
  },

  watch: {
    budget_etcs: {
      handler() {
        this.updateCharts();
      },
      deep: true
    }
  },

  async mounted() {
    await this.budget_etcQueryAll();
  },

  methods: {
    getCurrentYear() {
      return new Date().getFullYear() + 543;
    },

    formatNumber(num) {
      if (num === null || num === undefined || num === "") return "0";
      return Number(num).toLocaleString("th-TH");
    },

    calculatePercent(count) {
      if (!count || !this.totalEmployees || this.totalEmployees === 0) return 0;
      return parseFloat((count * 100) / this.totalEmployees).toFixed(2);
    },

    getPercentColor(percent) {
      const p = parseFloat(percent);
      if (p >= 50) return "success";
      if (p >= 25) return "warning";
      if (p >= 10) return "info";
      return "grey";
    },

    getCountColor(count) {
      const c = parseInt(count) || 0;
      if (c >= 1000) return "success";
      if (c >= 500) return "primary";
      if (c >= 100) return "info";
      return "grey";
    },

    getInsuranceColor(type) {
      if (!type) return "error";
      if (type.includes("มาตรา 33")) return "success";
      if (type.includes("มาตรา 39")) return "info";
      if (type.includes("มาตรา 40")) return "primary";
      if (type.includes("ไม่มี") || type === "") return "error";
      return "primary";
    },

    getInsuranceIcon(type) {
      if (!type) return "mdi-alert";
      if (type.includes("มาตรา 33")) return "mdi-shield-check";
      if (type.includes("มาตรา 39")) return "mdi-shield-account";
      if (type.includes("มาตรา 40")) return "mdi-shield-star";
      if (type.includes("ไม่มี") || type === "") return "mdi-shield-off";
      return "mdi-shield";
    },

    showSnackbar(color, icon, text) {
      this.snackbar = { show: true, color, icon, text, timeout: 5000 };
    },

    updateCharts() {
      const labels = this.budget_etcs.map(item => 
        item.personnel_etc_safety ? 
        (item.personnel_etc_safety.length > 25 ? item.personnel_etc_safety.substring(0, 25) + "..." : item.personnel_etc_safety) 
        : "ไม่ระบุ"
      );

      if (this.$refs.donutChart) {
        this.$refs.donutChart.updateOptions({
          labels: labels
        });
      }

      if (this.$refs.barChart) {
        this.$refs.barChart.updateOptions({
          xaxis: {
            categories: labels
          }
        });
      }
    },

    async budget_etcQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http.post("budget_etc.php", {
          ApiKey: this.ApiKey,
          budget_etcsafety: "ok"
        });
        
        this.budget_etcs = result.data || [];
        
        // Calculate total employees
        let counts = 0;
        this.budget_etcs.forEach(value => {
          counts += parseInt(value.countsafe) || 0;
        });
        this.totalEmployees = counts;

        this.$nextTick(() => {
          this.updateCharts();
        });
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการโหลดข้อมูล");
        console.error("Error loading data:", error);
      } finally {
        this.loading = false;
      }
    },

    // Export to Excel
    exportToExcel() {
      const exportData = this.budget_etcs.map(item => ({
        "ประเภทประกันสังคม": item.personnel_etc_safety || "ไม่ระบุ",
        "จำนวน (คน)": parseInt(item.countsafe) || 0,
        "ร้อยละ": this.calculatePercent(item.countsafe) + "%"
      }));

      // Add summary row
      exportData.push({
        "ประเภทประกันสังคม": "รวมทั้งหมด",
        "จำนวน (คน)": this.totalEmployees,
        "ร้อยละ": "100%"
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ข้อมูลประกันสังคม");

      // Set column widths
      const colWidths = [
        { wch: 40 },
        { wch: 15 },
        { wch: 15 }
      ];
      ws["!cols"] = colWidths;

      XLSX.writeFile(wb, `รายงานประกันสังคม_${this.getCurrentYear()}.xlsx`);
      this.showSnackbar("success", "mdi-check-circle", "ส่งออกไฟล์ Excel สำเร็จ");
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap');

.social-security-report {
  font-family: 'Sarabun', sans-serif;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #455A64 0%, #455A64 100%);
  border-radius: 16px;
  padding: 24px 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(25, 118, 210, 0.3);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}

.header-badges {
  gap: 8px;
}

.header-chip {
  font-weight: 500;
}

.total-chip {
  background: rgba(255, 255, 255, 0.95) !important;
}

/* Statistics Cards */
.stat-card {
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-primary { border-left-color: #455A64; }
.stat-card-success { border-left-color: #2E7D32; }
.stat-card-warning { border-left-color: #A86400; }
.stat-card-info { border-left-color: #455A64; }

.stat-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.stat-percent {
  font-size: 0.8rem;
  color: #999;
  margin: 0;
}

.stat-icon {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Chart Cards */
.chart-card {
  border-radius: 16px;
  overflow: hidden;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

/* Data Table Card */
.data-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  padding: 16px 24px;
}

.table-actions {
  gap: 8px;
}

.search-field {
  max-width: 400px;
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
}

.modern-table >>> th {
  background: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  color: #333 !important;
}

.modern-table >>> td {
  font-size: 0.875rem;
  padding: 16px !important;
}

.modern-table >>> tr:hover {
  background: #f5f5f5 !important;
}

.insurance-name {
  font-weight: 500;
  color: #333;
}

.percent-text {
  font-size: 0.65rem;
  font-weight: 600;
}

.percent-label {
  font-size: 0.875rem;
  color: #666;
}

/* Responsive */
@media (max-width: 960px) {
  .header-section {
    padding: 16px 20px;
  }
  
  .page-title {
    font-size: 1.25rem;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .table-actions {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card,
.chart-card,
.data-table-card {
  animation: fadeIn 0.5s ease forwards;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }
</style>